// @catalog F.006
// Hook reusable de persistencia en localStorage tipado para state de UI.
// Bloque 2 del plan dashboard_ux_ventas_margenes 2026-05-21 — usado por
// DashboardVentas + MarginsLevel1 para persistir selectedMonth y filtros.
import { useCallback, useEffect, useState } from "react";

export type SetValue<T> = (value: T | ((prev: T) => T)) => void;

interface UseLocalStorageStateOptions {
  // Permite omitir persistencia en escenarios SSR/test específicos.
  // Default: persiste cuando window está disponible.
  enabled?: boolean;
}

function readFromStorage<T>(key: string, initial: T): T {
  if (typeof window === "undefined") {
    return initial;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return initial;
    }
    return JSON.parse(raw) as T;
  } catch (err) {
    // JSON corrupto o storage bloqueado: log warning y fallback al initial.
    // No throw — el estado de UI no es config crítica.
    console.warn(
      `[useLocalStorageState] failed to parse key "${key}", using initial. Error:`,
      err,
    );
    return initial;
  }
}

export function useLocalStorageState<T>(
  key: string,
  initial: T,
  options?: UseLocalStorageStateOptions,
): [T, SetValue<T>] {
  const enabled = options?.enabled !== false;

  const [value, setValueState] = useState<T>(() =>
    enabled ? readFromStorage(key, initial) : initial,
  );

  const setValue = useCallback<SetValue<T>>(
    (next) => {
      setValueState((prev) => {
        const resolved =
          typeof next === "function"
            ? (next as (prev: T) => T)(prev)
            : next;
        if (enabled && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(key, JSON.stringify(resolved));
          } catch (err) {
            console.warn(
              `[useLocalStorageState] failed to persist key "${key}". Error:`,
              err,
            );
          }
        }
        return resolved;
      });
    },
    [enabled, key],
  );

  // Sync entre tabs/pestañas: cuando otro tab actualiza la misma key,
  // refrescamos el estado local. Sin esto, dos pestañas abiertas divergen
  // y la decisión de la lluvia "persistencia mes/filtros localStorage por usuario"
  // se rompe en escenarios multi-pestaña.
  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }
    const handler = (event: StorageEvent) => {
      if (event.key !== key || event.storageArea !== window.localStorage) {
        return;
      }
      if (event.newValue === null) {
        setValueState(initial);
        return;
      }
      try {
        setValueState(JSON.parse(event.newValue) as T);
      } catch (err) {
        console.warn(
          `[useLocalStorageState] storage event with corrupt value for key "${key}":`,
          err,
        );
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [enabled, key, initial]);

  return [value, setValue];
}
